import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { heroSlides, heroConfig } from '../../data';
import './Hero.css';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, heroConfig.autoPlayInterval);

    return () => clearInterval(timer);
  }, []);

  const scrollToSection = useCallback((selector) => {
    const element = document.querySelector(selector);
    if (element) {
      const offset = 90;
      const elementPosition = element.getBoundingClientRect().top;
      window.scrollTo({
        top: elementPosition + window.pageYOffset - offset,
        behavior: 'smooth',
      });
    }
  }, []);

  const slide = heroSlides[currentSlide];

  return (
    <section id="home" className="hero">
      {/* Background slideshow with slow zoom */}
      <div className="hero-backdrop">
        <AnimatePresence>
          <motion.div
            key={slide.id}
            className="hero-slide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: heroConfig.transitionDuration / 1000, ease: 'easeInOut' }}
          >
            <motion.img
              src={slide.image}
              alt=""
              className="hero-slide-image"
              initial={{ scale: 1 }}
              animate={{ scale: 1.09 }}
              transition={{ duration: (heroConfig.autoPlayInterval + heroConfig.transitionDuration) / 1000, ease: 'linear' }}
            />
          </motion.div>
        </AnimatePresence>
        <div className="hero-overlay" />
      </div>

      {/* Content */}
      <div className="hero-content-wrapper">
        <div className="container">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id}
              className="hero-content"
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.span
                className="hero-eyebrow"
                variants={{
                  hidden: { opacity: 0, y: 14 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.15 } },
                  exit: { opacity: 0, transition: { duration: 0.3 } },
                }}
              >
                {slide.subtitle}
              </motion.span>

              <h1 className="hero-title">
                {slide.title.split(' ').map((word, i) => (
                  <span className="hero-word-mask" key={`${slide.id}-${i}`}>
                    <motion.span
                      className="hero-word"
                      variants={{
                        hidden: { y: '110%' },
                        visible: {
                          y: 0,
                          transition: { duration: 0.8, delay: 0.25 + i * 0.055, ease: [0.16, 1, 0.3, 1] },
                        },
                        exit: { y: '-110%', transition: { duration: 0.35, delay: i * 0.02 } },
                      }}
                    >
                      {word}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                className="hero-description"
                variants={{
                  hidden: { opacity: 0, y: 18 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.55 } },
                  exit: { opacity: 0, transition: { duration: 0.3 } },
                }}
              >
                {slide.description}
              </motion.p>

              <motion.div
                className="hero-actions"
                variants={{
                  hidden: { opacity: 0, y: 18 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.7 } },
                  exit: { opacity: 0, transition: { duration: 0.3 } },
                }}
              >
                <button className="btn btn-solid" onClick={() => scrollToSection('#contact')}>
                  Start Your Project <span className="btn-arrow">→</span>
                </button>
                <button className="btn btn-ghost-light" onClick={() => scrollToSection('#services')}>
                  Explore Services
                </button>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom bar: counter + line indicators + scroll hint */}
      <div className="hero-bottom">
        <div className="container hero-bottom-inner">
          <div className="hero-counter">
            <span className="counter-current">0{currentSlide + 1}</span>
            <span className="counter-divider" />
            <span className="counter-total">0{heroSlides.length}</span>
          </div>

          <div className="hero-indicators">
            {heroSlides.map((s, index) => (
              <button
                key={s.id}
                className={`hero-indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              >
                <span className="indicator-track">
                  {index === currentSlide && (
                    <motion.span
                      className="indicator-fill"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: heroConfig.autoPlayInterval / 1000, ease: 'linear' }}
                    />
                  )}
                </span>
              </button>
            ))}
          </div>

          <button className="hero-scroll" onClick={() => scrollToSection('#about')}>
            <span>Scroll</span>
            <motion.span
              className="hero-scroll-line"
              animate={{ scaleY: [0, 1, 0], originY: ['0', '0', '1'] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
